/**
 * Formulario reutilizable para crear un placement (inserción) de un aviso.
 * Usado en el detalle del aviso. Reusa OutletPicker + PeruGeoPicker +
 * PriceEstimatePreview. Valida la cascada geo igual que el wizard.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, X, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { OutletPicker } from "@/components/feature/media/outlet-picker";
import { PeruGeoPicker, type GeoValue } from "@/components/feature/media/peru-geo-picker";
import { PriceEstimatePreview } from "@/components/feature/media/price-estimate-preview";
import { media, type Coverage, type ContentFormat } from "@/lib/api";

type FormState = {
  outlet_id: number | null;
  coverage: Coverage;
  geo: GeoValue;
  requested_at: string;
  scheduled_at: string;
  end_at: string;
  payment_at: string;
  duration_days: string;
  repetitions: string;
  spot_duration_seconds: string;
  word_count: string;
  content_format: ContentFormat | "";
  cost_amount: string;
  price_amount: string;
};

const EMPTY: FormState = {
  outlet_id: null,
  coverage: "nacional",
  geo: { region_id: null, province_id: null, district_id: null },
  requested_at: "", scheduled_at: "", end_at: "", payment_at: "",
  duration_days: "", repetitions: "1",
  spot_duration_seconds: "", word_count: "", content_format: "",
  cost_amount: "", price_amount: "",
};

function validate(f: FormState): string | null {
  if (!f.outlet_id) return "Selecciona un medio";
  if (f.coverage === "regional" && !f.geo.region_id) return "Cobertura regional requiere región";
  if ((f.coverage === "local" || f.coverage === "zona")) {
    if (!f.geo.region_id) return `Cobertura ${f.coverage} requiere región`;
    if (!f.geo.province_id) return `Cobertura ${f.coverage} requiere provincia`;
  }
  if (f.coverage === "nacional" && (f.geo.region_id || f.geo.province_id || f.geo.district_id)) {
    return "Cobertura nacional no debe tener región/provincia/distrito";
  }
  return null;
}

export function PlacementForm({
  adId, onCreated, onCancel,
}: {
  adId: number;
  onCreated?: () => void;
  onCancel?: () => void;
}) {
  const qc = useQueryClient();
  const [f, setF] = useState<FormState>(EMPTY);

  const create = useMutation({
    mutationFn: () => media.createPlacement({
      ad_id: adId,
      outlet_id: f.outlet_id!,
      coverage: f.coverage,
      region_id: f.geo.region_id ?? undefined,
      province_id: f.geo.province_id ?? undefined,
      district_id: f.geo.district_id ?? undefined,
      requested_at: f.requested_at || undefined,
      scheduled_at: f.scheduled_at || undefined,
      end_at: f.end_at || undefined,
      payment_at: f.payment_at || undefined,
      duration_days: f.duration_days ? Number(f.duration_days) : undefined,
      repetitions: f.repetitions ? Number(f.repetitions) : 1,
      spot_duration_seconds: f.spot_duration_seconds ? Number(f.spot_duration_seconds) : undefined,
      word_count: f.word_count ? Number(f.word_count) : undefined,
      content_format: f.content_format || undefined,
      cost_amount: f.cost_amount ? Number(f.cost_amount) : undefined,
      price_amount: f.price_amount ? Number(f.price_amount) : undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media", "placements", "by_ad", adId] });
      toast.success("Placement creado");
      setF(EMPTY);
      onCreated?.();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function submit() {
    const err = validate(f);
    if (err) { toast.error(err); return; }
    create.mutate();
  }

  function set(patch: Partial<FormState>) { setF({ ...f, ...patch }); }

  return (
    <div className="border border-brand-200 rounded-lg p-4 space-y-3 bg-brand-50/30 dark:bg-brand-900/10">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <OutletPicker
          value={f.outlet_id}
          onChange={(outlet_id) => set({ outlet_id })}
          label="Medio *"
        />
        <div>
          <Label className="text-xs">Cobertura *</Label>
          <Select value={f.coverage} onValueChange={(v) => set({
            coverage: v as Coverage,
            geo: { region_id: null, province_id: null, district_id: null },
          })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="nacional">Nacional</SelectItem>
              <SelectItem value="regional">Regional</SelectItem>
              <SelectItem value="local">Local</SelectItem>
              <SelectItem value="zona">Zona</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <PeruGeoPicker coverage={f.coverage} value={f.geo} onChange={(geo) => set({ geo })} />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <Label className="text-xs">Fecha solicitud</Label>
          <DatePicker value={f.requested_at} onChange={(e) => set({ requested_at: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs">Fecha programación</Label>
          <DatePicker value={f.scheduled_at} onChange={(e) => set({ scheduled_at: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs">Fecha finalización</Label>
          <DatePicker value={f.end_at} onChange={(e) => set({ end_at: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs">Fecha de pago</Label>
          <DatePicker value={f.payment_at} onChange={(e) => set({ payment_at: e.target.value })} />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <Label className="text-xs">Duración (días)</Label>
          <Input type="number" min="0" value={f.duration_days}
            onChange={(e) => set({ duration_days: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs">Repeticiones</Label>
          <Input type="number" min="1" value={f.repetitions}
            onChange={(e) => set({ repetitions: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs">Spot (s)</Label>
          <Input type="number" min="0" value={f.spot_duration_seconds}
            onChange={(e) => set({ spot_duration_seconds: e.target.value })}
            placeholder="30, 60, 90" />
        </div>
        <div>
          <Label className="text-xs">Palabras</Label>
          <Input type="number" min="0" value={f.word_count}
            onChange={(e) => set({ word_count: e.target.value })} />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs">Formato</Label>
          <Select value={f.content_format} onValueChange={(v) => set({ content_format: v as ContentFormat })}>
            <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1/8">1/8 página</SelectItem>
              <SelectItem value="1/4">1/4 página</SelectItem>
              <SelectItem value="1/2">1/2 página</SelectItem>
              <SelectItem value="full_page">Página completa</SelectItem>
              <SelectItem value="30s">Spot 30s</SelectItem>
              <SelectItem value="60s">Spot 60s</SelectItem>
              <SelectItem value="90s">Spot 90s</SelectItem>
              <SelectItem value="custom">Personalizado</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Costo (PEN) — vacío = auto</Label>
          <Input type="number" min="0" step="0.01" value={f.cost_amount}
            onChange={(e) => set({ cost_amount: e.target.value })}
            placeholder="Auto-calc desde tarifario" />
        </div>
        <div>
          <Label className="text-xs">Precio (PEN) — visible al cliente</Label>
          <Input type="number" min="0" step="0.01" value={f.price_amount}
            onChange={(e) => set({ price_amount: e.target.value })}
            placeholder="Vacío = sin monto para el cliente" />
        </div>
      </div>

      <PriceEstimatePreview
        outletId={f.outlet_id}
        wordCount={f.word_count ? Number(f.word_count) : null}
        spotDurationSeconds={f.spot_duration_seconds ? Number(f.spot_duration_seconds) : null}
        repetitions={Number(f.repetitions) || 1}
      />

      <div className="flex justify-end gap-2 pt-1">
        {onCancel && (
          <Button variant="outline" size="sm" onClick={onCancel}>
            <X size={14} /> Cancelar
          </Button>
        )}
        <Button size="sm" onClick={submit} disabled={create.isPending}>
          {create.isPending ? "Guardando…" : <><Check size={14} /> Guardar placement</>}
        </Button>
      </div>
    </div>
  );
}
